import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileDown, ExternalLink } from "lucide-react";
import { inr, formatDate } from "@/lib/utils2";

function fyStart() {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-04-01`;
}
function fyEnd() {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() + 1 : d.getFullYear();
  return `${y}-03-31`;
}

function KPI({ label, value, accent = "" }) {
  return (
    <div className="bg-secondary/40 border border-brand-line rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">{label}</div>
      <div className={`mt-1 font-serif text-2xl tabular-nums ${accent || "text-brand-ink"}`}>{value}</div>
    </div>
  );
}

export default function BillRegister() {
  const [period, setPeriod] = usePersistedState("period:breg", () => presetRange("this-fy"));
  const [partyId, setPartyId] = usePersistedState("filters:breg:party", "all");
  const [billType, setBillType] = usePersistedState("filters:breg:type", "all");
  const [parties, setParties] = useState([]);
  const [data, setData] = useState({ bills: [], totals: {} });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: period.from, date_to: period.to };
      if (partyId !== "all") params.party_id = partyId;
      if (billType !== "all") params.bill_type = billType;
      const [res, pp] = await Promise.all([
        api.get("/bill-register", { params }),
        parties.length ? Promise.resolve({ data: parties }) : api.get("/parties"),
      ]);
      setData(res.data);
      if (!parties.length) setParties(pp.data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period.from, period.to, partyId, billType]);

  const downloadPdf = async () => {
    const params = { date_from: period.from, date_to: period.to };
    if (partyId !== "all") params.party_id = partyId;
    if (billType !== "all") params.bill_type = billType;
    const res = await api.get("/bill-register-pdf", { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `Bill-Register-${period.from}_to_${period.to}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadBundlePdf = async () => {
    if (partyId === "all") return;
    const res = await api.get(`/party/${partyId}/bills-bundle-pdf`, {
      params: { date_from: period.from, date_to: period.to }, responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    const partyName = parties.find((p) => p.id === partyId)?.name?.replace(/\s+/g, "_") || "party";
    a.href = url; a.download = `Bills-Bundle-${partyName}-${period.from}_to_${period.to}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data.totals || {};

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1500px] mx-auto" data-testid="bill-register-page">
      <PageHeader
        title="Bill Register"
        subtitle="A complete bill-wise sales register for any date range — full GST breakup, receipts, balance, and printable PDF."
        action={
          <div className="flex gap-2">
            <Button variant="outline" data-testid="download-bill-register-pdf-btn" onClick={downloadPdf} className="rounded-sm border-brand-ink">
              <FileDown className="h-4 w-4 mr-1.5" /> Download Register PDF
            </Button>
            <Button data-testid="download-bills-bundle-pdf-btn" onClick={downloadBundlePdf}
              disabled={partyId === "all"} className="rounded-sm bg-brand-forest text-white disabled:opacity-50">
              <FileDown className="h-4 w-4 mr-1.5" /> Download Bills Bundle (single party)
            </Button>
          </div>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
              defaultPreset="this-fy" testIdPrefix="bill-reg-period" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-brand-muted">Party</Label>
              <Select value={partyId} onValueChange={setPartyId}>
                <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white" data-testid="filter-bill-party">
                  <SelectValue placeholder="All parties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All parties</SelectItem>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-brand-muted">Type</Label>
              <Select value={billType} onValueChange={setBillType}>
                <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white" data-testid="filter-bill-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="transport">Transport</SelectItem>
                  <SelectItem value="combined">Combined</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KPI label="Bills" value={totals.count || 0} />
        <KPI label="Subtotal" value={inr(totals.subtotal || 0)} />
        <KPI label="GST (CGST+SGST)" value={inr((totals.cgst || 0) + (totals.sgst || 0))} />
        <KPI label="Total Billed" value={inr(totals.total_amount || 0)} />
        <KPI label="Outstanding" value={inr(totals.balance || 0)}
             accent={totals.balance > 0 ? "text-brand-terracotta" : "text-brand-forest"} />
      </div>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bill-register-table">
            <thead>
              <tr className="bg-brand-forest text-white text-left text-[11px] uppercase tracking-[0.12em]">
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Bill #</th>
                <th className="px-3 py-3">Party</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3 text-right">GST</th>
                <th className="px-3 py-3 text-right">Subtotal</th>
                <th className="px-3 py-3 text-right">Tax</th>
                <th className="px-3 py-3 text-right">Total</th>
                <th className="px-3 py-3 text-right">Received</th>
                <th className="px-3 py-3 text-right">Balance</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="py-8 text-center text-brand-muted">Loading…</td></tr>
              )}
              {!loading && (data.bills || []).length === 0 && (
                <tr><td colSpan={12} className="py-10 text-center text-brand-muted">
                  No bills match these filters. Adjust the date range or party.
                </td></tr>
              )}
              {(data.bills || []).map((b) => (
                <tr key={b.id} className="border-b border-brand-line hover:bg-secondary/30">
                  <td className="px-3 py-2.5 whitespace-nowrap">{formatDate(b.bill_date)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{b.bill_number}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{b.party_name}</div>
                    {b.party_gstin && <div className="text-[10px] text-brand-muted">{b.party_gstin}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${
                      b.bill_type === "combined" ? "bg-brand-sand/15 border-brand-sand text-brand-ink" : ""
                    }`}>
                      {b.bill_type === "material" ? "Material" : b.bill_type === "combined" ? "Combined" : "Transport"}
                    </Badge>
                    <div className="text-[10px] text-brand-muted mt-0.5">{b.line_count} truck(s)</div>
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs">{b.gst_percent}%</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(b.subtotal)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-brand-muted">
                    {inr((b.cgst || 0) + (b.sgst || 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(b.total_amount)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-brand-forest">{inr(b.received_total)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={b.balance_due > 0 ? "text-brand-terracotta font-medium" : "text-brand-forest"}>
                      {inr(b.balance_due)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge className={`rounded-sm text-[10px] ${
                      b.status === "SETTLED" ? "bg-brand-forest text-white" :
                      b.status === "PARTIAL" ? "bg-brand-sand text-brand-ink" :
                      "bg-brand-terracotta text-white"
                    }`}>{b.status}</Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/bills/${b.id}`} className="inline-flex items-center gap-1 text-xs text-brand-muted hover:text-brand-ink">
                      <ExternalLink className="h-3 w-3" /> Open
                    </Link>
                  </td>
                </tr>
              ))}
              {(data.bills || []).length > 0 && (
                <tr className="bg-secondary/60 font-semibold">
                  <td colSpan={5} className="px-3 py-3 text-[11px] uppercase tracking-[0.15em]">
                    Totals ({totals.count} bills)
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{inr(totals.subtotal)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{inr((totals.cgst || 0) + (totals.sgst || 0))}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{inr(totals.total_amount)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-brand-forest">{inr(totals.received)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className={totals.balance > 0 ? "text-brand-terracotta" : "text-brand-forest"}>
                      {inr(totals.balance)}
                    </span>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
